import {
  Component,
  Input,
  Output,
  EventEmitter,
  ChangeDetectionStrategy,
  inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzToolTipModule } from 'ng-zorro-antd/tooltip';
import { MemberAvatarComponent, MemberIdentity } from '@shared/components/member-avatar/member-avatar.component';
import { MentionTextComponent } from '@shared/components/mention-text/mention-text.component';
import { type IEBNotification } from '@shared/models/entities/notification.interface';
import { TranslateService } from '@core/services/translate.service';
import { TranslatePipe } from '@shared/pipes/translate.pipe';

@Component({
  selector: 'app-notification-item',
  standalone: true,
  imports: [
    CommonModule,
    NzButtonModule,
    NzIconModule,
    NzToolTipModule,
    MemberAvatarComponent,
    MentionTextComponent,
    TranslatePipe,
  ],
  templateUrl: './notification-item.component.html',
  styleUrls: ['./notification-item.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NotificationItemComponent {
  private static readonly RELATIVE_UNITS: ReadonlyArray<readonly [Intl.RelativeTimeFormatUnit, number]> = [
    ['year', 31_536_000_000],
    ['month', 2_592_000_000],
    ['week', 604_800_000],
    ['day', 86_400_000],
    ['hour', 3_600_000],
    ['minute', 60_000],
  ];

  private translate = inject(TranslateService);
  @Input() notification!: IEBNotification;
  @Input() actorIdentity: MemberIdentity | null = null;
  @Input() actorDisplayName: string | null = null;

  @Output() markRead = new EventEmitter<string>();
  @Output() markUnread = new EventEmitter<string>();
  @Output() navigate = new EventEmitter<string>();

  handleClick(): void {
    if (!this.notification.readAt) {
      this.markRead.emit(this.notification.notificationId);
    }
    this.navigate.emit(this.notification.notificationId);
  }

  handleKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.handleClick();
    }
  }

  toggleRead(event: Event): void {
    event.stopPropagation();

    if (this.notification.readAt) {
      this.markUnread.emit(this.notification.notificationId);
      return;
    }

    this.markRead.emit(this.notification.notificationId);
  }

  /** Compact, locale-aware relative timestamp (e.g. "4 Tg.", "2 Std."). */
  formatTime(date: Date): string {
    const justNow = this.translate.instant('notification.time.just_now');
    const timestamp = new Date(date).getTime();

    if (Number.isNaN(timestamp)) {
      return justNow;
    }

    const elapsed = Date.now() - timestamp;
    if (elapsed < 60_000) {
      return justNow;
    }

    try {
      const formatter = new Intl.RelativeTimeFormat(this.translate.locale, {
        numeric: 'always',
        style: 'narrow',
      });

      for (const [unit, unitMs] of NotificationItemComponent.RELATIVE_UNITS) {
        if (elapsed >= unitMs) {
          return formatter.format(-Math.floor(elapsed / unitMs), unit);
        }
      }
    } catch {
      return justNow;
    }

    return justNow;
  }

  /** Only show severity tag for warning / error (not info) */
  showSeverity(): boolean {
    const s = this.notification.severity?.toLowerCase();
    return s === 'error' || s === 'warning';
  }

  notificationTone(): 'info' | 'warning' | 'destructive' {
    const severity = this.notification.severity?.toLowerCase() || 'info';

    if (severity === 'error') {
      return 'destructive';
    }

    if (severity === 'warning') {
      return 'warning';
    }

    return 'info';
  }

  severityIcon(): string {
    const tone = this.notificationTone();

    if (tone === 'destructive') {
      return 'close-circle';
    }

    if (tone === 'warning') {
      return 'exclamation-circle';
    }

    return 'info-circle';
  }

  severityLabel(): string {
    const severity = this.notification.severity?.toLowerCase();
    if (severity === 'error') {
      return this.translate.instant('notification.severity.important');
    }

    if (severity === 'warning') {
      return this.translate.instant('notification.severity.warning');
    }

    return this.translate.instant('notification.severity.info');
  }

  notificationTypeIcon(): string {
    const type = this.notification.type?.toLowerCase() || '';

    if (type.includes('mention')) {
      return 'comment';
    }

    if (type.includes('assign')) {
      return 'appstore';
    }

    if (type.includes('comment') || type.includes('thread')) {
      return 'message';
    }

    return 'notification';
  }
}
